import { Icon } from '@/modules/Icon';
import { AvatarStatusProps, AvatarStatusColor, AvatarStatusSize } from '../types';
import { faBell } from '@fortawesome/pro-solid-svg-icons';
import { cn } from '@/shared/utils/cn';
import type { IconType, AnyIconDefinition } from '@/shared/types/types';

/**
 * ============================================
 * AVATAR STATUS - ATOMIC COMPONENT
 * ============================================
 * Indicador de estado para el Avatar
 *
 * @component
 * @example
 * ```tsx
 * // Status de color
 * <AvatarStatus type="color" color="success" size="md" />
 *
 * // Status con icono
 * <AvatarStatus type="icon" icon={faBell} size="md" />
 * ```
 */

// Tamaños del status según Figma
const STATUS_SIZES: Record<AvatarStatusSize, string> = {
  sm: 'ui-w-8 ui-h-8',
  md: 'ui-w-12 ui-h-12',
  lg: 'ui-w-16 ui-h-16',
};

// Tamaño del borde según Figma
const BORDER_SIZES: Record<AvatarStatusSize, string> = {
  sm: 'ui-border-1',
  md: 'ui-border-2',
  lg: 'ui-border-2',
};

// Colores del status según Figma
const STATUS_COLORS: Record<AvatarStatusColor, string> = {
  brand: 'ui-bg-fill-brand',
  success: 'ui-bg-fill-success',
  warning: 'ui-bg-fill-warning',
  danger: 'ui-bg-fill-danger',
  disabled: 'ui-bg-fill-disabled',
};

// Tamaño del icono según el tamaño del status
// Se usa 'xs' (10px) y se escala con CSS para que quepa dentro del círculo
const ICON_SIZES: Record<AvatarStatusSize, 'xs' | 'sm' | 'md'> = {
  sm: 'xs',
  md: 'xs',
  lg: 'xs',
};

export const AvatarStatus = ({
  type = 'color',
  color = 'brand',
  size = 'md',
  icon,
}: AvatarStatusProps) => {
  const sizeClasses = STATUS_SIZES[size];
  const borderClasses = BORDER_SIZES[size];

  if (type === 'icon') {
    // Escalar el icono para que quepa dentro del círculo
    // sm: 8px círculo (con borde 1px = 6px interno) -> icono ~4-5px
    // md: 12px círculo (con borde 2px = 8px interno) -> icono ~6-7px
    // lg: 16px círculo (con borde 2px = 12px interno) -> icono ~8-9px
    const iconScale: Record<AvatarStatusSize, number> = {
      sm: 0.4, // 10px * 0.4 = 4px
      md: 0.6, // 10px * 0.6 = 6px
      lg: 0.8, // 10px * 0.8 = 8px
    };

    return (
      <div
        className={cn(
          'ui-absolute ui-bottom-0 ui-right-0',
          'ui-flex ui-items-center ui-justify-center',
          'ui-rounded-full',
          'ui-border-solid ui-border-on-color ui-bg-fill',
          'ui-shadow-01',
          sizeClasses,
          borderClasses
        )}
      >
        <div
          className="ui-flex ui-items-center ui-justify-center"
          style={{ transform: `scale(${iconScale[size]})` }}
        >
          <Icon
            name={(icon || faBell) as IconType | AnyIconDefinition}
            size={ICON_SIZES[size]}
            color="brand"
          />
        </div>
      </div>
    );
  }

  // type === 'color'
  return (
    <div
      className={cn(
        'ui-absolute ui-bottom-0 ui-right-0',
        'ui-rounded-full',
        'ui-border-solid ui-border-on-color',
        'ui-shadow-01',
        sizeClasses,
        borderClasses,
        STATUS_COLORS[color]
      )}
    />
  );
};
